import { NbfcLink, linkifyNbfc } from "../NbfcLink";
import React from "react";
import Footer from "../Footer";
import Navbar from "../Navbar";

const CancellationRefundPolicy: React.FC = () => {
  const policySections = [
    {
      title: `Cancellation & Refund Policy – ${import.meta.env.VITE_COMPANY_NAME}`,
      content: `At ${import.meta.env.VITE_COMPANY_NAME}, operated by ${import.meta.env.VITE_COMPANY_NBFC}, we value your trust and are committed to maintaining a transparent and customer-friendly lending experience. This policy outlines our approach towards loan cancellations and refunds for borrowers using our platform.`,
    },
    {
      title: "1. Pre-Disbursement Cancellation",
      content: `If you change your mind before the loan amount is disbursed, you may cancel your approved application at any time without incurring any additional charges or penalties.`,
    },
    {
      title: "2. Post-Disbursement Cancellation",
      content: `If the loan amount has already been credited to your account, you may still request cancellation within 24 hours of disbursement by returning the entire principal amount to ${import.meta.env.VITE_COMPANY_NAME}. Once repayment confirmation is received, your loan will be marked as cancelled in our system.`,
    },
    {
      title: "3. Refund of Excess Payments",
      content: `Any overpayments made towards your loan account will be reconciled and refunded within 5–7 working days after review. Refunds are processed electronically to the original payment source used for repayment.`,
    },
    {
      title: "4. Processing & Timelines",
      list: [
        "Refunds are initiated only after internal verification and reconciliation.",
        "The timeline for refund reflection depends on your bank or payment gateway policy.",
        "Refund confirmation will be communicated via email/SMS registered with the company.",
      ],
    },
    {
      title: "5. Exceptions",
      list: [
        "Loans once disbursed cannot be cancelled beyond 24 hours of credit.",
        "Interest, processing fees, or service charges are non-refundable once the loan has been activated.",
        "Duplicate transactions or technical payment errors will be refunded after due verification.",
      ],
    },
    {
      title: "6. Contact & Resolution",
      content: `If you wish to raise a cancellation or refund request, please contact our support team at your registered email or through the support channels mentioned on our website. All valid refund or cancellation claims will be processed within the regulatory timelines.`,
    },
    {
      title: "Acknowledgment",
      content: `By availing our services, you acknowledge that you have read, understood, and agreed to this Cancellation & Refund Policy. Thank you for choosing ${import.meta.env.VITE_COMPANY_NAME} — your reliable partner for transparent and responsible financial services.`,
    },
  ];

  return (
    <div className="min-w-screen bg-[var(--background-gradient-middle)]">
      <div className="max-w-[1440px] mx-auto flex flex-col gap-12 px-4 sm:px-6 lg:px-12 py-16">
        <Navbar />

        {/* Header */}
        <div className="text-center mt-12 relative z-10 w-full mb-2 mt-12">
          <h1 className="text-4xl md:text-7xl font-[900] leading-none tracking-tighter mb-4 bg-gradient-to-r from-[var(--primary)] to-[var(--secondary-accent)] text-transparent bg-clip-text">
            Cancellation & Refund Policy
          </h1>
          <p className="text-[var(--text-gray)] font-bold text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            At{" "}
            <span className="font-semibold text-[var(--primary)] text-xl">
              {import.meta.env.VITE_COMPANY_NAME}
            </span>
            , operated by{" "}
            <span className="text-[var(--primary)] text-xl">
              <NbfcLink />
            </span>
            , we strive to maintain transparency and fairness in all customer
            transactions. This policy explains the conditions for loan
            cancellations and refund processing.
          </p>
        </div>

        {/* Policy Content */}
        <div className="max-w-4xl w-full mx-auto">
          <div className="bg-white/40 backdrop-blur-3xl rounded-[32px] md:rounded-[40px] border-2 border-white/50 shadow-[20px_20px_40px_rgba(0,0,0,0.04)] p-8 md:p-12 space-y-10">
            {policySections.map((section, idx) => (
              <div key={idx} className="space-y-4">
                <h2 className="text-2xl font-[900] text-[var(--text-primary)] tracking-tight mb-2 border-b-2 border-[var(--primary)]/20 pb-2">
                  {linkifyNbfc(section.title)}
                </h2>
                {section.content && (
                  <p className="text-[var(--text-gray)] text-base whitespace-pre-line leading-relaxed font-medium">
                    {linkifyNbfc(section.content)}
                  </p>
                )}
                {section.list && (
                  <ul className="list-disc list-outside ml-6 space-y-2 text-[var(--text-gray)] text-base font-medium">
                    {section.list.map((li, i) => (
                      <li key={i} className="pl-1">{linkifyNbfc(li)}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </div>

        <Footer />
      </div>
    </div>
  );
};

export default CancellationRefundPolicy;
